import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useWorkspaceStore } from "@/hooks/state";
import { useWorkspaces } from "@/hooks/api/useWorkspaces";
import { getUserIdFromJWT } from "@/services/auth";
import { workspaceService } from "@/services/workspace";
import { queryKeys } from "@/lib/queryKeys";
import { STALE } from "@/lib/queryPolicy";

/**
 * Hook that encapsulates workspace loading, syncing, and switching logic.
 * Uses React Query (via useWorkspaces) for caching — no flash on re-mount.
 */
export function useWorkspaceSwitcher() {
  const { workspaceId, setWorkspaceId, isSuperadmin } = useWorkspaceStore();
  const { data: workspaces = [], isLoading } = useWorkspaces();

  const currentUserId = useMemo(() => getUserIdFromJWT(), []);

  const inMemberList = useMemo(
    () => (workspaceId ? (workspaces.find((w: any) => w.id === workspaceId) ?? null) : null),
    [workspaceId, workspaces],
  );

  // Superadmin "view as": the active workspace can be one the admin isn't a
  // member of (deep-linked from the internal dashboard). Fetch it by id so the
  // header resolves the real account + name instead of falling back to the
  // admin's own first workspace. The API bypasses membership for superadmins.
  const { data: adoptedWorkspace } = useQuery({
    queryKey: queryKeys.workspace.detail(workspaceId!),
    queryFn: () => workspaceService.getWorkspace(workspaceId!),
    enabled: !!workspaceId && !inMemberList && !isLoading && isSuperadmin,
    staleTime: STALE.stable,
  });

  const currentWorkspace = useMemo(() => {
    if (inMemberList) return inMemberList;
    if (adoptedWorkspace) return adoptedWorkspace;
    return workspaces[0] ?? null;
  }, [inMemberList, adoptedWorkspace, workspaces]);

  /**
   * `to` names where the switch should LAND, instead of reloading whatever route
   * you happened to be on. A freshly created workspace belongs in the wizard, and
   * `assign` gets it there directly — reloading `/sources` first and letting the
   * console gate bounce it works too, but the reader watches the bounce.
   *
   * Same full navigation either way, so every "state is updated" guarantee the
   * 500ms delay buys (workspace access recorded, persisted query cache cleared)
   * is unchanged.
   */
  const switchWorkspace = (workspace: any, opts?: { to?: string }) => {
    if (workspace.id === workspaceId && !opts?.to) return;

    setWorkspaceId(workspace.id);

    setTimeout(() => {
      if (opts?.to) window.location.assign(opts.to);
      else window.location.reload();
    }, 500);
  };

  return {
    workspaces,
    currentWorkspace,
    isLoading,
    switchWorkspace,
    currentUserId,
  };
}
